import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, map, of, Subject, switchMap } from 'rxjs';
import { filter, first, shareReplay, startWith, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { CommissionWizardComponent, CommissionWizardComponentDialogData } from '../dave-commission-module/components/commission-wizard/commission-wizard.component';
import {
    createCommissionFormGroup,
    getCommissionFormSubscriptions,
    getCommissionLocationOptions, ICommissionDetailListTemplateEntrys,
    resetCommissionFormGroup,
    saveCommission,
    setCommissionDetailListTemplateEntries,
} from '../dave-commission-module/components/detail-commission/helper';
import { CommissionResolver } from '../dave-data-module/guards/commission.resolver';
import { CommissionTypeResolver } from '../dave-data-module/guards/commissionType.resolver';
import { CustomerResolver } from '../dave-data-module/guards/customer.resolver';
import { OfficeResolver } from '../dave-data-module/guards/office.resolver';
import { UserToCommissionResolver } from '../dave-data-module/guards/user-to-commission.resolver';
import { CustomerDataService, CustomerSearchState } from '../dave-data-module/services/customer-data.service';
import { State } from '../dave-data-module/State';
import { BaseActionTypes } from '../dave-data-module/State/actions/base.actions';
import { CommissionActionTypes } from '../dave-data-module/State/actions/commission.actions';
import {
    getCommissionById,
    getCommissions,
    getCommissionsFetched,
} from '../dave-data-module/State/selectors/commission.selector';
import { getCommissionTypeById, getCommissionTypes, getCommissionTypesFetched } from '../dave-data-module/State/selectors/commissionType.selectors';
import { getCustomersFetched, getNotDeletedCustomers } from '../dave-data-module/State/selectors/customers.selectors';
import { getEntityRole, getEntityRoleFetched } from '../dave-data-module/State/selectors/entity-role.selector';
import { getOffices, getOfficesFetched } from '../dave-data-module/State/selectors/offices.selectors';
import {
    getPartner,
    getPartnerOfficeDictionary,
    getPartnerOffices,
} from '../dave-data-module/State/selectors/partners.selectors';
import {
    getUserToCommissionFetched,
    getUserToCommissions,
} from '../dave-data-module/State/selectors/user-to-commission.selector';
import { getUser, getUserDictionary, getUsers } from '../dave-data-module/State/selectors/users.selectors';
import { SelectSearchData } from '../dave-utils-module/select-search/components/select-search-legacy/select-search-legacy.component';
import { Address } from '../helper/helper';
import { CustomLabelService } from './custom-label/custom-label.service';
import { getFetched$ } from '../dave-data-module/helper/helper';
import { EntityRoleResolver } from '../dave-data-module/guards/entity-role.resolver';
import { getPersons } from '../dave-data-module/State/selectors/person.selectors';
import { CommissionWizardSteps } from '../dave-data-module/entities/commissionType.entity';
import { getDepartmentsActive } from '../dave-data-module/State/selectors/department.selector';
import { DepartmentEntity } from '../dave-data-module/entities/department.entity';
import { Dictionary } from '@ngrx/entity';
import { getEmployees } from '../dave-data-module/State/selectors/employees.selectors';
import { getStatusFromBackoffice } from '../dave-data-module/State/selectors/statusFromBackoffice.selectors';
import { getJobSpecification } from '../dave-data-module/State/selectors/job-specification.selector';
import {
    CUSTOMIZATION_FUNCTIONS_COMMISSION_SHAREPOINT,
    CustomizationDataService,
} from '../dave-data-module/services/customization-data.service';
import { sharepointFolderNameValidator } from '../helper/validation.helper';



@Injectable({
    providedIn: 'root',
})
export class CommissionWizardDialogService {
    constructor(
        private store: Store<State>,

        private dialog: MatDialog,

        private cls: CustomLabelService,
        private commissionRes: CommissionResolver,
        private commissionTypeRes: CommissionTypeResolver,
        private customerRes: CustomerResolver,
        private customerDataService: CustomerDataService,
       // private officeRes: OfficeResolver,
        private userToCommissionResolver: UserToCommissionResolver,
        private entityRoleResolver: EntityRoleResolver,
        private actions$: Actions,

        private router: Router,
        private customizationDataService: CustomizationDataService,
    ) {
        this.customizationDataService
            .getFeatures$()
            .pipe(take(1))
            .subscribe((features) => {
                if (features?.includes('hideSuppliersInCommission')) {

                    this.customerSearch = this.customerDataService.createSearchState({
                        onlyCustomers: true,
                    });
                }
            });
    }
    private customerSearch: CustomerSearchState = this.customerDataService.createSearchState();
    OpenDialog(commissionId?: number, WizardSteps?: CommissionWizardSteps[], parentCommissionId?: number) {
        const userToCommissionFromCommission$ = getFetched$(this.store, getUserToCommissionFetched, getUserToCommissions, this.userToCommissionResolver).pipe(map((u2cs) => u2cs.filter((u2c) => u2c.CommissionId === commissionId)));
        const commissionEntityRoles$ = combineLatest([userToCommissionFromCommission$, this.store.select(getUser)]).pipe(
            map(([u2cs, user]) => u2cs?.filter((u) => u.UserId === user.Id)),
            switchMap((u2c) => getFetched$(this.store, getEntityRoleFetched, getEntityRole, this.entityRoleResolver).pipe(map((er) => er.filter((e) => u2c.some((u) => u.RoleId === e.Id))))),
        );
        return firstValueFrom(
            combineLatest([
                // this.actions$.pipe(ofType(CommissionActionTypes.UpdateOne, BaseActionTypes.ErrorAction)),
                getFetched$(this.store, getCommissionTypesFetched, getCommissionTypes, this.commissionTypeRes),
                this.store.select(getPartner),
                this.cls.getSingle$('Customer'),
                commissionEntityRoles$,
                this.store.select(getPersons),
                this.customizationDataService.getFunctions$().pipe(shareReplay({refCount: true, bufferSize: 1})),
            ]),
        ).then(([commissionTypes, partner, CustomerName, entityRoles, persons, functions]) => {
            const form = createCommissionFormGroup(functions.some(f => CUSTOMIZATION_FUNCTIONS_COMMISSION_SHAREPOINT.some(s => s === f.Slug)) ? [sharepointFolderNameValidator(), Validators.required] : null);
            if (commissionId == null) {
                form.controls.AutomaticAuftragsnummer.setValue(true);
            }
            const CommissionLocationForm = new FormControl<Address>({
                Street: '',
                PostalCode: '',
                City: '',
                Country: '',
            });
            const SearchLocationForm = new FormControl<SelectSearchData>(null);

            const dialogBeforeClosed = new Subject<void>();

            let dialogRef: MatDialogRef<CommissionWizardComponent>;
            const subs = [...getCommissionFormSubscriptions(form, this.customerDataService) /*...getCommissionFormAddressSearchSubscriptions(form, this.store, CommissionLocationForm, SearchLocationForm)*/];
            dialogBeforeClosed.subscribe(() => {
                subs.forEach((s) => s.unsubscribe());
            });

            const commission$ = commissionId != null
                ? getFetched$(this.store, getCommissionsFetched, getCommissionById({ id: commissionId }), this.commissionRes).pipe(shareReplay({refCount: true, bufferSize: 1}))
                : of(null);
            if (commissionId != null) {
                commission$
                    .pipe(takeUntil(dialogBeforeClosed))
                    .subscribe(async (commission) => {
                        resetCommissionFormGroup(commission, form, this.customerDataService);
                    });
            }
            if (parentCommissionId != null){
                form.controls.ParentCommission.setValue({Id: parentCommissionId});
            }
            const detailListTemplateEntries$ = combineLatest([
                commission$,
                form.controls.CommissionTypeId.valueChanges.pipe(
                    startWith(form.controls.CommissionTypeId.value),
                    switchMap((ct) => (ct?.Id ? this.store.select(getCommissionTypeById({ id: ct.Id })) : of(null))),
                ),
                this.cls.getSingle$('Commission'),
                this.cls.getLabels$('Commission'),
                this.cls.getMultiple$('CustomerSupplier'),
                getCommissionLocationOptions(this.store),
                this.cls.labels$,
                this.store.select(getDepartmentsActive),
                this.store.select(getCommissions),
                this.store.select(getPartnerOffices),
                this.store.select(getPartnerOfficeDictionary),
                this.store.select(getUserDictionary),
                this.store.select(getEmployees),
                this.store.select(getStatusFromBackoffice),
                this.store.select(getJobSpecification)
            ]).pipe(
                takeUntil(dialogBeforeClosed),
                map(([commission, currentCommissionTypeEntity, commissionLabel, commissionLabels, customersLabel, commissionLocationOptions, customLabels, departmentsEntity, commissions, partnerOffices, partnerOfficeDict, users, employees, statusBackoffice, jobSpecs]) => {
                    const detailListTemplateEntries: ICommissionDetailListTemplateEntrys = {};
                    const departments: Dictionary<DepartmentEntity> =
                        Object.fromEntries(
                            departmentsEntity.map(d => [d.Id, d])
                        );
                    setCommissionDetailListTemplateEntries(
                        commission ?? ({} as any),
                        detailListTemplateEntries,
                        [
                            'PlanedWorkingHours',
                            'CompleteBusinessVolume',
                            'CostsCompleteBusinessVolume',
                            'TravelZoneId',
                            'AbgabeterminAG',
                            'AbgabeterminQM',
                            'LetzteFrist',
                            'NachlassZulage',
                            'VorarbeiterBonus',
                            'Customer',
                            'CommissionType',
                            'Description',
                            'CommissonNumber',
                            'CommissonNumberAuto',
                            'InterneNummer',
                            'InterneNummerAuto',
                            'CustomerLocation',
                            'Location',
                            'Auftragseingang',
                            'StartDate',
                            'PlanedStartDate',
                            'EndDate',
                            'PlanedEndDate',
                            'Schadensdatum',
                            'Versicherungssumme',
                            'Schadennummer',
                            'Versicherungsnummer',
                            'AutomaticAccountsReceivableLedger',
                            'AutomaticAccountsReceivableLedgerByClockIns',
                            'ProjectInfo',
                            'OrderNoCustomer',
                            'CompleteBusinessVolume',
                            'CostsCompleteBusinessVolume',
                            'PlanedWorkingHours',
                            'OfferSubmissionDate',
                            'NegotiationAt',
                            'Person',
                            'ParentCommission',
                            'ProjectAdmin',
                            'StatusBackofficeId',
                            'Kostenstellenart',
                            'ProjectManager'
                        ],
                        form,
                        entityRoles,
                        {
                            CustomerName,
                           // customers,
                          //  offices,
                            commissionType: null,
                            commissionTypes,
                            partner,
                            currentCommissionTypeEntity,
                            currentCommissionType: currentCommissionTypeEntity,
                            SearchLocationForm,
                            CommissionLocationForm,
                            commissionLocationOptions,
                            persons,
                            departments,
                            commissions,
                            partnerOffices,
                            users,
                            employees,
                            statusBackoffice,
                            jobSpecs,
                            customerSearch: {
                                customerOptions$: this.customerSearch.customerOptions$,
                                customerOptionsLoading$: this.customerSearch.loading$,
                                searchCustomers: (search) => this.customerSearch.SearchCustomers(search, 'unbenannter' + customersLabel),
                                customerPageIndex$: this.customerSearch.customerPaging.pageIndex$,
                                customerPageCount$: this.customerSearch.customerPaging.pageCount$,
                                customerPageChange: (event) =>
                                    this.customerSearch.customerPageChange(event),
                            }
                        },
                        commissionLabel,
                        commissionLabels.wordBinding,
                        commissionLabels.wordBinding + 'art',
                        commissionLabels.wordBinding + 'nr.',
                        customersLabel,
                        customLabels,
                    );
                    detailListTemplateEntries.DepartmentId = {
                        key: customLabels?.ExecutingDepartment?.Nominativ?.singular?.value || 'Abteilung',
                        formControl: form.controls.DepartmentId,
                        options: {
                            specialInput: {
                                singleSelectSearch: { options: departmentsEntity.map(option => ({ optionLabel: option.optionLabel + (option.PartnerOfficeId ? ' ('+partnerOfficeDict[option.PartnerOfficeId].Name+')' : ''), Id: option.Id })), compareOptions: (a, b) => a?.Id === b?.Id },
                            },
                        },
                    };
                    return detailListTemplateEntries;
                }),
            );
            return firstValueFrom(detailListTemplateEntries$).then((detailListTemplateEntries) => {
                console.log('open wizard');
                dialogRef = this.dialog.open<CommissionWizardComponent, CommissionWizardComponentDialogData>(CommissionWizardComponent, {
                    ...CommissionWizardComponent.DefaultConfig,
                    data: {
                        commissionId: commissionId,
                        detailListTemplateEntries,
                        statusBackofficeForm: form.controls.StatusBackofficeId,
                        wizardSteps: WizardSteps,
                        partnerOfficeForm: form.controls.PartnerOffice,
                    },
                });
                dialogRef.beforeClosed().subscribe(() => dialogBeforeClosed.next());
                dialogRef.componentInstance.Save.pipe(
                    takeUntil(dialogBeforeClosed),
                    switchMap(({ wizardPage, routerLink }) => {
                        const currentCommissionId = dialogRef.componentInstance.CommissionId;
                        if (currentCommissionId == null) {
                            return of(null);
                        }
                        return this.store.select(getCommissionById({ id: currentCommissionId })).pipe(
                            first(),
                            map((commission) => ({ wizardPage, routerLink, commission })),
                        );
                    }),
                    filter((v) => v != null),
                ).subscribe(({ wizardPage, routerLink, commission }) => {
                    if (routerLink) {
                        firstValueFrom(this.actions$.pipe(ofType(CommissionActionTypes.UpdateMany, BaseActionTypes.ErrorAction))).then((action) => {
                            if (action.type === CommissionActionTypes.UpdateMany.type) {
                                this.router.navigate(routerLink);
                            }
                        });
                    }
                    saveCommission(this.store, commission, wizardPage, form, CommissionLocationForm);
                    if (wizardPage === null) {
                        // wizard finished
                        dialogRef.close();
                    }
                });
                return dialogRef;
            });
        });
    }
}
